import Footer from '@buddieshr/components/footer';
import Header from '@buddieshr/components/header';
import MetaHeader from '@buddieshr/components/metaHeader';
import PageWrapper from '@buddieshr/components/templates/pageWrapper';
import { useRouter } from 'next/router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '@buddieshr/styles/security.module.scss';
import { Alert, Divider } from 'antd';
import Link from 'next/link';
import {
  DatabaseOutlined,
  FileProtectOutlined,
  FileTextOutlined,
  LinkOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { SECURITY_POLICIES } from '@buddieshr/utils/constants';

const Vignette = ({ children, icon, title }) => (
  <div className={styles.vignette}>
    {icon && <div className={styles.vignetteIcon}>{icon}</div>}
    {title && <div className={styles.vignetteTitle}>{title}</div>}
    <div className={styles.vignetteContent}>{children}</div>
  </div>
);

const Section = ({ children }) => (
  <div className={styles.section}>{children}</div>
);

const Item = ({ question, answer }) => (
  <div className={styles.itemWrapper}>
    <div className={styles.itemQuestion}>{question}</div>
    <div className={styles.itemAnswer}>{answer}</div>
  </div>
);

const Security = () => {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <>
      <MetaHeader
        title={'Security Practices and Data Protection | BuddiesHR'}
        description={t('descriptionContentSecurity')}
        currentURL={`https://buddieshr.com/${
          router.locale === 'en' ? '' : router.locale
        }/security`}
        locale={router.locale}
        pageSlug="security"
      />
      <Header />
      <main className={styles.main}>
        <div className={styles.header}>
          <PageWrapper width={800}>
            <div className={styles.mainHeader}>
              <h1 className={styles.title}>Security</h1>
              <p className={styles.subtitle}>
                Find all information about our security processes, server
                locations and best practises in place.
              </p>
            </div>
            <Divider />
            <br />
            <Section>
              <h2>
                <DatabaseOutlined />
                &nbsp;&nbsp;Data
              </h2>
              <Item
                question="Data collection"
                answer="BuddiesHR will only collect the data needed for the
                operation of its apps."
              />
              <Item
                question="Data encryption"
                answer="We follow the OWASP best practices and encrypt data (both at
                    rest and in transit)."
              />
              <Item
                question="Data storage"
                answer="We do not store any data that we don’t need for our
                operations."
              />
              <Item
                question="Data deletion"
                answer="All data related
                  to the Slack workspace are automatically deleted 12 months after any app is
                  removed from the Slack workspace."
              />
              <Item
                question="Servers location"
                answer="AWS data center located in Paris, France (eu-west-3)."
              />
              <Item question="Data hosting country" answer="France" />
              <Item question="Data hosting company" answer="AWS" />
            </Section>
            <Divider />
            <Section>
              <br />
              <h2>
                <FileProtectOutlined />
                &nbsp;&nbsp;Security policies
              </h2>
              <div>
                Contact us to receive any of these security policies. (
                <a href="mailto:security@buddieshr.com">
                  security@buddieshr.com
                </a>
                )
                <div>
                  <ul className={styles.policies}>
                    {SECURITY_POLICIES.map((secu) => (
                      <li key={secu} className={styles.secuWrapper}>
                        <div className={styles.secuIcon}>
                          <FileProtectOutlined />
                        </div>
                        <div className={styles.secuContent}>{secu}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Section>
            <Divider />
            <Section>
              <h2>
                <SafetyCertificateOutlined />
                &nbsp;&nbsp;Certifications
              </h2>
              <div>
                <Item question="HIPAA" answer="No" />
                <Item
                  question="SOC2"
                  answer={
                    <div>
                      <div style={{ marginBottom: 12 }}>No</div>
                      <Alert
                        type="info"
                        showIcon
                        description="The CTO of BuddiesHR has previously overseen SOC2 certification and continues to adhere to all SOC2 principles."
                      />
                    </div>
                  }
                />
                <Item
                  question="Security Questionnaire"
                  answer="$500, on-demand"
                />
              </div>
              <Alert
                showIcon
                type="warning"
                description="Before starting any security review process,
            please make sure to test the apps you want to install using a test Slack
            workspace (it is free to create a Slack workspace and free to try BuddiesHR apps)."
              />
            </Section>
            <Section>
              <h2>
                <LinkOutlined />
                &nbsp;&nbsp;Useful links
              </h2>
              <div className={styles.vignetteList}>
                <Vignette icon={<LockOutlined />} title={'Privacy policy'}>
                  You can access our privacy policies with this link:&nbsp;
                  <Link href="/privacy">Privacy policies</Link>
                </Vignette>
                <Vignette icon={<FileTextOutlined />} title="Terms & condition">
                  You can access our terms and conditions with this link:&nbsp;
                  <Link href="/terms-conditions">View Terms & conditions</Link>
                </Vignette>
              </div>
            </Section>
            <Section>
              <h2>
                <QuestionCircleOutlined />
                &nbsp;&nbsp;Any question?
              </h2>
              <div>
                Contact us at{' '}
                <a href="mailto:security@buddieshr.com">
                  security@buddieshr.com
                </a>
              </div>
            </Section>
          </PageWrapper>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Security;
